import React from "react";
import dayjs from "dayjs";
import {
  Box,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import { SortType } from "../../../models/SortType";
import { StorageCostDetailItem } from "../../../models/StorageCost/StorageCost";

interface StorageCostDetailsListingTableProps {
  detailRows: StorageCostDetailItem[];
  isLoading: boolean;
  sortBy: string;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);


const StorageCostDetailsListingTable: React.FC<StorageCostDetailsListingTableProps> = ({
  detailRows,
  isLoading,
  sortBy,
  sortType,
  onSortChange,
}) => {
  const theme = useTheme<Theme>();

  if (isLoading) {
    return (
      <Container sx={{ minHeight: "25vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (detailRows.length === 0) {
    return (
      <Paper
        sx={{
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ maxHeight: window.innerHeight - 350 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell title="Sr No" sx={{ maxWidth: "100px" }} />
              <AppTableHeaderCell title="Date" sortColumn="date"
                sortBy={sortBy}
                sortDirection={sortType}
                onSortChange={onSortChange} />
              <AppTableHeaderCell title="Partner Tag" />
              <AppTableHeaderCell
                title="Pack Label"
                sx={{
                  minWidth: "200px",
                  maxWidth: "300px",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              />
              <AppTableHeaderCell title="Sku" sx={{ minWidth: "100px", maxWidth: "120px", whiteSpace: "nowrap" }} />
              <AppTableHeaderCell title="Quantity" sx={{ minWidth: "100px", maxWidth: "120px", whiteSpace: "nowrap" }} />
              <AppTableHeaderCell title="Box Size" sx={{ minWidth: "100px", maxWidth: "120px", whiteSpace: "nowrap" }} />
              <AppTableHeaderCell title="Monthly Cost" sx={{ minWidth: "100px", maxWidth: "120px", whiteSpace: "nowrap" }} />
              <AppTableHeaderCell title="Total / Day" sx={{ minWidth: "100px", maxWidth: "120px", whiteSpace: "nowrap" }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {detailRows.map((row, index) => (
              <TableRow
                key={`${row.id}-${row.date || index}-${index}`}
                hover
                sx={{
                  "&:nth-of-type(odd)": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <AppTableRowCell>{index + 1}</AppTableRowCell>
                <AppTableRowCell>{dayjs(row.date).format("DD MMM, YYYY")}</AppTableRowCell>
                <AppTableRowCell>{row.partner_tag || "-"}</AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "200px",
                    maxWidth: "300px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {row.label || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "100px",
                    maxWidth: "120px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {row.size || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "100px",
                    maxWidth: "120px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {row.quantity}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "100px",
                    maxWidth: "120px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {row.box_size}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "100px",
                    maxWidth: "120px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatCurrency(row.monthly_storage_cost)}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    minWidth: "100px",
                    maxWidth: "120px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatCurrency(row.total_cost_per_day)}
                </AppTableRowCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default StorageCostDetailsListingTable;
